import { jwtDecode } from 'jwt-decode'

import { mint } from '@web-app/lib/auth/client'
import type { Token } from '@web-app/lib/ironSession'

// Refresh before expiry so an operation never flies with a nearly-dead token.
// Undecodable/absent tokens answer false — the backend enforces, we schedule.
const EXPIRY_LEEWAY_MS = 60 * 1000

export const tokenIsExpiring = (
  token: string | undefined,
  nowMs: number,
): boolean => {
  if (!token) return false
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token)
    if (!exp) return false
    return exp * 1000 - nowMs <= EXPIRY_LEEWAY_MS
  } catch {
    return false
  }
}

export type MintResult = 'ok' | 'dead' | 'unavailable' | 'mismatch'

type TokenMachineDeps = {
  getUserId: () => string | undefined
  reload: () => void
}

// One machine per browser client: the credential and its mint state die with
// the client that owns them.
export const createTokenMachine = ({ getUserId, reload }: TokenMachineDeps) => {
  let latestToken: Token | undefined
  // One mint per tab: concurrent operations await the same in-flight request;
  // the auth client's lock extends the guarantee across tabs.
  let mintInFlight: Promise<MintResult> | null = null
  let forcedMintInFlight: Promise<MintResult> | null = null

  // Pure: stores the token and reports the result, never navigates.
  const mintToken = async (forceRefresh: boolean): Promise<MintResult> => {
    const response = await mint({ forceRefresh })

    if (response.status === 401) {
      // Dead session — the mint route already destroyed the cookie.
      return 'dead'
    }

    if (!response.ok) {
      // Transport trouble says nothing about the credential — fail open.
      return 'unavailable'
    }

    const { token, userId } = (await response.json()) as {
      token: string
      userId?: string
    }
    // Token minted for another user — a sibling tab switched the shared cookie.
    if (userId && userId !== getUserId()) {
      return 'mismatch'
    }
    latestToken = token
    return 'ok'
  }

  // A mint that throws (every attempt timed out, offline) is 'unavailable'.
  const runMint = (forceRefresh: boolean): Promise<MintResult> =>
    mintToken(forceRefresh).catch(() => 'unavailable' as MintResult)

  const getOrMintToken = (): Promise<MintResult> => {
    mintInFlight ??= runMint(false).finally(() => {
      mintInFlight = null
    })
    return mintInFlight
  }

  // A rejected Bearer bypasses the cookie cache; otherwise the mint would hand
  // back the very token that just failed.
  const forceMintToken = (): Promise<MintResult> => {
    forcedMintInFlight ??= runMint(true).finally(() => {
      forcedMintInFlight = null
    })
    return forcedMintInFlight
  }

  const ensureFreshToken = async (): Promise<void> => {
    if (!latestToken) {
      // Boot-mint: a hard load starts tokenless and the first operation mints.
      // On 'unavailable' it flies tokenless; the next operation retries.
      if (getUserId()) {
        const result = await getOrMintToken()
        if (result === 'dead' || result === 'mismatch') {
          reload()
          await new Promise<never>(() => undefined)
        }
      }
      return
    }
    if (!tokenIsExpiring(latestToken, Date.now())) return
    const result = await getOrMintToken()
    if (result === 'dead' || result === 'mismatch') {
      reload()
      await new Promise<never>(() => undefined)
    }
  }

  // undefined means "no credential right now" — a tokenless flight, not death.
  const getFreshAccessToken = async (): Promise<Token | undefined> => {
    await ensureFreshToken()
    return latestToken
  }

  return {
    forceMintToken,
    ensureFreshToken,
    getFreshAccessToken,
    getToken: () => latestToken,
  }
}
